import {
	Component,
	forwardRef,
	HostBinding,
	input,
	OnDestroy,
	OnInit,
} from '@angular/core';
import {
	ControlValueAccessor,
	FormControl,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { AsyncSubject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface YesNoOption {
	label: string;
	value: boolean | typeof NULL_OPTION_SENTINEL;
}

const NULL_OPTION_SENTINEL = '__NULL_OPTION__';

@Component({
	selector: 'ae-yes-no-select',
	templateUrl: './yes-no-select.component.html',
	styleUrls: ['./yes-no-select.component.scss'],
	imports: [NgSelectModule, ReactiveFormsModule],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => YesNoSelectComponent),
			multi: true,
		},
	],
})
export class YesNoSelectComponent
	implements ControlValueAccessor, OnInit, OnDestroy
{
	@HostBinding('class.custom-form-control') customFormControl = true;
	private _unsubscribe$ = new AsyncSubject<null>();

	readonly yesLabel = input<string>('Yes');
	readonly noLabel = input<string>('No');
	readonly placeholder = input<string>('');
	readonly clearable = input<boolean>(true);
	readonly nullOptionValue = input<{
		label: string;
		value: boolean | null;
		prepend?: boolean;
		default?: boolean;
	} | null>(null);

	protected control = new FormControl<
		boolean | typeof NULL_OPTION_SENTINEL | null
	>(null);
	protected options: YesNoOption[] = [];

	private _touchFunction: () => void;
	private _changeFunction: (value: boolean | null) => void = () => null;
	private _changeWatcher: Subscription;

	ngOnInit(): void {
		this.options = [
			{ label: this.yesLabel(), value: true },
			{ label: this.noLabel(), value: false },
		];

		if (this.nullOptionValue()) {
			const nullOption = this.nullOptionValue();

			const option: YesNoOption = {
				label: nullOption.label,
				value:
					nullOption.value === null ? NULL_OPTION_SENTINEL : nullOption.value,
			};

			if (nullOption.prepend) {
				this.options.unshift(option);
			} else {
				this.options.push(option);
			}
		}

		this.watchForChange();
	}

	ngOnDestroy(): void {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}

	private watchForChange = () => {
		if (this._changeWatcher) return;

		this._changeWatcher = this.control.valueChanges
			.pipe(takeUntil(this._unsubscribe$))
			.subscribe((val) => {
				const mappedValue = val === NULL_OPTION_SENTINEL ? null : val;

				if (this._changeFunction) {
					this._changeFunction(mappedValue);
				}

				if (this._touchFunction) {
					this._touchFunction();
				}
			});
	};

	private stopWatchForChange = () => {
		this._changeWatcher?.unsubscribe();
		this._changeWatcher = null;
	};

	public writeValue(val: boolean | null): void {
		try {
			this.stopWatchForChange();
			this.control.setValue(
				val === null && this.nullOptionValue() ? NULL_OPTION_SENTINEL : val,
			);
		} catch (e) {
			throw new Error(`YesNoSelectComponent.writeValue could not set value`, {
				cause: e,
			});
		} finally {
			this.watchForChange();
		}
	}

	public registerOnChange(fn: (value: unknown) => void): void {
		this._changeFunction = fn;
	}

	public registerOnTouched(fn: () => void): void {
		this._touchFunction = fn;
	}

	public setDisabledState(isDisabled: boolean): void {
		this.stopWatchForChange();

		if (isDisabled) {
			this.control.disable();
		} else {
			this.control.enable();
		}

		this.watchForChange();
	}
}
